import type { OAuthProviderId } from "@/services/api";

export const OAUTH_POPUP_MESSAGE = "vendor-oauth-callback" as const;

/** OIDC state value for silent authorize requests (must match API) */
export const OAUTH_SILENT_STATE = "silent";

/** Errors where prompt=none failed — caller should fall back to interactive login */
const SILENT_FALLBACK_ERROR_CODES = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
]);

export function isSilentFallbackOAuthError(errorCode: string): boolean {
  return SILENT_FALLBACK_ERROR_CODES.has(errorCode);
}

export type OAuthPopupMessage =
  | {
      type: typeof OAUTH_POPUP_MESSAGE;
      status: "success";
      provider: OAuthProviderId;
      code: string;
    }
  | {
      type: typeof OAUTH_POPUP_MESSAGE;
      status: "error";
      provider: OAuthProviderId;
      error: string;
      /** When true, do not show an error — retry with interactive login */
      silentFallback?: boolean;
    };

const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 700;
const SILENT_POPUP_WIDTH = 420;
const SILENT_POPUP_HEIGHT = 520;

export function openOAuthPopup(
  url: string,
  provider: OAuthProviderId,
  options?: { silent?: boolean },
): Window | null {
  const width = options?.silent ? SILENT_POPUP_WIDTH : POPUP_WIDTH;
  const height = options?.silent ? SILENT_POPUP_HEIGHT : POPUP_HEIGHT;
  const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - height) / 2);

  return window.open(
    url,
    `oauth-${provider}${options?.silent ? "-silent" : ""}`,
    [
      "popup=yes",
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "noopener=no",
      "noreferrer=no",
    ].join(","),
  );
}

export function postOAuthResultToOpener(message: OAuthPopupMessage): boolean {
  if (!window.opener || window.opener.closed) {
    return false;
  }

  window.opener.postMessage(message, window.location.origin);
  return true;
}

export function isOAuthPopupMessage(data: unknown): data is OAuthPopupMessage {
  if (!data || typeof data !== "object") return false;
  const msg = data as OAuthPopupMessage;
  return (
    msg.type === OAUTH_POPUP_MESSAGE &&
    (msg.status === "success" || msg.status === "error") &&
    (msg.provider === "discord" || msg.provider === "feedforward")
  );
}

export function listenForOAuthPopupResult(
  onResult: (message: OAuthPopupMessage) => void,
): () => void {
  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (!isOAuthPopupMessage(event.data)) return;
    onResult(event.data);
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}

export function watchOAuthPopupClosed(
  popup: Window,
  onClosed: () => void,
): () => void {
  const intervalId = window.setInterval(() => {
    if (popup.closed) {
      window.clearInterval(intervalId);
      onClosed();
    }
  }, 300);

  return () => window.clearInterval(intervalId);
}
